import { accentColors, lightTokens } from '~tokens/internal'

/**
 * Returns a color based on the score value (1-5 ratio scale)
 */
export const getScoreColor = (score: number, maxScore: number): string => {
  const ratio = score / maxScore
  if (ratio <= 0.2) return lightTokens.error.main
  if (ratio <= 0.4) return lightTokens.warning.main
  if (ratio <= 0.6) return accentColors.amber.main
  if (ratio <= 0.8) return accentColors.teal.main
  return lightTokens.success.main
}

/**
 * Returns a themed color based on the category type
 */
export const getCategoryColor = (category: string): string => {
  const categoryLower = category.toLowerCase()

  const categoryColorMap: Record<string, string> = {
    appeals_and_dispute: accentColors.indigo.main,
    application_and_form_question: accentColors.purple.main,
    appointment_and_scheduling: accentColors.teal.main,
    complaint: accentColors.blue.main,
    contact_and_routing: accentColors.pink.main,
    document_and_form_request: accentColors.deepPurple.main,
    eligibility_question: accentColors.amber.main,
    emergency_and_urgent: accentColors.orange.main,
    feedback: accentColors.green.main,
    other: accentColors.red.main,
    out_of_scope: accentColors.primary.main,
    payment_and_fees: accentColors.neutral.main,
    policy_question: accentColors.lime.main,
    process_and_how_to: accentColors.neutral.dark,
    service_request: accentColors.blue.dark,
    status_check: accentColors.blue.main,

    // Legacy categories (kept for backward compatibility with existing evaluated records)
    policy_and_procedure: accentColors.indigo.main,
    ordinance_and_code: accentColors.purple.main,
    resolution: accentColors.teal.main,
    standard_operating_procedure: accentColors.blue.main,
    application_and_form: accentColors.pink.main,
    guide_and_manual: accentColors.deepPurple.main,
    plan_and_strategy: accentColors.amber.main,
    rule_and_regulation: accentColors.orange.main,
    fee_schedule_and_rate_table: accentColors.green.main,
    permitting_and_licensing_requirement: accentColors.red.main,
    public_service_process: accentColors.primary.main,
    procurement_procedure: accentColors.neutral.main,
    charter_and_bylaw: accentColors.lime.main,
    organizational_chart: accentColors.neutral.dark,
    grant_and_program_guideline: accentColors.blue.dark,
  }

  return categoryColorMap[categoryLower] || accentColors.neutral.main
}

/**
 * Returns a background color with opacity for category chips
 */
export const getCategoryBackgroundColor = (category: string): string => {
  const mainColor = getCategoryColor(category)
  return `${mainColor}33`
}

/**
 * Formats an enum label by replacing underscores with spaces and capitalizing the first letter.
 * e.g. "policy_and_procedure" → "Policy and procedure"
 */
export const formatEnumLabel = (value: string): string => {
  return value.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase())
}

/**
 * Returns chip config for sentiment display
 */
export const getSentimentChipConfig = (
  sentiment: string,
): {
  iconName:
    | 'SentimentVerySatisfiedOutlined'
    | 'SentimentDissatisfiedOutlined'
    | 'SentimentSatisfiedOutlined'
  backgroundColor: string
  color: string
} => {
  switch (sentiment.toLowerCase()) {
    case 'positive':
      return {
        iconName: 'SentimentVerySatisfiedOutlined',
        backgroundColor: lightTokens.success.light,
        color: lightTokens.success.main,
      }
    case 'negative':
      return {
        iconName: 'SentimentDissatisfiedOutlined',
        backgroundColor: lightTokens.error.lighter,
        color: lightTokens.error.main,
      }
    case 'neutral':
    default:
      return {
        iconName: 'SentimentSatisfiedOutlined',
        backgroundColor: lightTokens.background.background2,
        color: lightTokens.text.secondary,
      }
  }
}
